import { CircleHelp } from 'lucide-react';

import { cn } from '../../lib/cn';
import { Z } from '../../tokens/z-index';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover/Popover';

export interface LabelHintProps {
  /** Explicação exibida por clique, toque ou teclado. Consumer passa valor de t(). */
  hint: string;
  /** Lado preferido do balão. Default `top`. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

/**
 * LabelHint — ícone "?" ao lado do texto de um label de formulário que exibe
 * uma explicação em balão acionável por clique, toque ou teclado.
 *
 * Renderizado automaticamente pelos componentes de form de @alamo/ui que
 * expõem a prop `labelHint` (Input, NumberInputWithUnit). Para campos com
 * `<label>` manual, usar diretamente: `<LabelHint hint={t('...')} />`.
 *
 * O conteúdo usa `Z.DROPDOWN_OVER_MODAL` porque hints aparecem com frequência
 * dentro de Dialogs (Z.MODAL = 400).
 */
export function LabelHint({ hint, side = 'top', className }: LabelHintProps) {
  // O NBSP cola o ícone à última palavra do label: line break é proibido em
  // volta de U+00A0, então o ícone nunca quebra sozinho pra linha seguinte.
  // Ele também substitui o espaçamento que antes vinha de `ml-1`.
  return (
    <span className="whitespace-nowrap">
      {'\u00A0'}
      <Popover>
        <PopoverTrigger>
          <button
            type="button"
            aria-label={hint}
            className={cn(
              'inline-flex shrink-0 cursor-help items-center align-middle',
              'text-slate-400 transition-colors hover:text-slate-600',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-full',
              className,
            )}
          >
            <CircleHelp className="h-3.5 w-3.5" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          role="tooltip"
          side={side}
          zIndex={Z.DROPDOWN_OVER_MODAL}
          className="max-w-xs min-w-0 bg-secondary-950 px-3 py-2 font-body text-xs text-white"
        >
          {hint}
        </PopoverContent>
      </Popover>
    </span>
  );
}
